import React, { useEffect, useRef } from 'react';
import { useCourierInfo } from './hooks/useCourierInfo';
import { useCourierRentals } from '@/hooks/useCourierRentals';
import { useCourierPayments } from '@/hooks/useCourierPayments';
import { useBikeDetails } from '@/hooks/useBikeDetails';
import { usePendingPayments } from './hooks/usePendingPayments';
import { useAuthPayment } from '@/contexts/AuthPaymentContext';
import { DashboardHeader } from './components/DashboardHeader';
import { PendingPaymentsCard } from './components/PendingPaymentsCard';
import { DashboardMainGrid } from './components/DashboardMainGrid';
import { AuthPaymentSection } from './components/AuthPaymentSection';
import { UpcomingSubscriptionPaymentCard } from './components/UpcomingSubscriptionPaymentCard';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';

const CourierDashboard = () => {
  // Получаем контекст авторизации
  const { user } = useAuth();
  
  // Используем реф для отслеживания первоначальной загрузки
  const initialLoadCompleted = useRef(false);
  
  // Получаем данные из контекста авторизационных платежей
  const { authPayment } = useAuthPayment();
  
  // Получаем данные о курьере из useCourierInfo - тут мы правильно получаем courier.id из таблицы couriers
  const { 
    courierId, 
    courierName, 
    bikeId, 
    loading: courierLoading,
    error: courierError
  } = useCourierInfo();
  
  // Используем полученный courier.id из таблицы couriers для загрузки аренд
  const { 
    rentals,
    currentRental, 
    isLoading: rentalsLoading,
    error: rentalsError
  } = useCourierRentals();
  
  // Используем полученный courier.id из таблицы couriers для загрузки платежей
  const { 
    payments, 
    totalPaid, 
    loading: paymentsLoading,
    error: paymentsError
  } = useCourierPayments(courierId);
  
  // Получаем данные о велосипеде либо из bikeId курьера, либо из текущей аренды
  const bikeIdToUse = bikeId || (currentRental?.bikeId);
  const { 
    bikeDetails, 
    loading: bikeLoading,
    error: bikeError
  } = useBikeDetails(bikeIdToUse);

  // УБРАНО: хук useRentalDocuments больше не нужен, так как URL документов формируется напрямую в ActiveRentalCard

  // Находим ожидающие платежи
  const { pendingPayments } = usePendingPayments(payments);
  
  // Функция для обновления данных велосипеда
  const handleBikeUpdate = () => {
    // Принудительно обновляем данные велосипеда
    window.location.reload();
  };
  
  // Эффект для логирования данных и отображения ошибок
  // Используем useRef для предотвращения повторных запросов при маунте компонента
  useEffect(() => {
    // Выполняем только при первой загрузке или при существенном изменении данных
    if (!initialLoadCompleted.current || (user?.id && courierId)) {
      console.log("CourierDashboard данные:", {
        user: user?.id, // ID пользователя из auth.users
        courierId, // ID курьера из таблицы couriers
        rentals: rentals?.length,
        currentRental: currentRental?.id,
        bikeId: bikeIdToUse,
        bikeDetails: bikeDetails?.id,
        payments: payments?.length,
        totalPaid, // Добавляем логирование суммы платежей
        pendingPayments: pendingPayments?.length, // Добавляем логирование ожидающих платежей
        hasAuthPayment: authPayment !== null // Добавляем логирование наличия авторизационного платежа
      });
      
      initialLoadCompleted.current = true;
    }
    
    // Показываем сообщения об ошибках только если они есть
    if (courierError) {
      toast.error(`Ошибка при загрузке данных курьера: ${courierError}`);
    }
    
    if (rentalsError) {
      toast.error(`Ошибка при загрузке данных аренд: ${rentalsError}`);
    }
    
    if (bikeError) {
      toast.error(`Ошибка при загрузке данных о велосипеде: ${bikeError}`);
    }
    
    if (paymentsError) {
      toast.error(`Ошибка при загрузке данных о платежах: ${paymentsError}`);
    }
  }, [user?.id, courierId]);

  return (
    <div className="space-y-6">
      {/* Заголовок панели управления */}
      <DashboardHeader courierName={courierName} />
      
      {/* Блок с авторизацией карты */}
      <AuthPaymentSection />
      
      {/* Блок с предстоящими платежами по подпискам */}
      <UpcomingSubscriptionPaymentCard />
      
      {/* Блок с ожидающими платежами, показывается только если они есть */}
      <PendingPaymentsCard pendingPayments={pendingPayments} />
      
      {/* Основная сетка компонентов */}
      <DashboardMainGrid 
        currentRental={currentRental}
        rentals={rentals}
        bikeDetails={bikeDetails}
        loading={courierLoading}
        rentalsLoading={rentalsLoading}
        bikeLoading={bikeLoading}
        payments={payments}
        totalPaid={totalPaid}
        paymentsLoading={paymentsLoading}
        onBikeUpdate={handleBikeUpdate}
      />
    </div>
  );
};

export default CourierDashboard;
